/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */
import type { NodeProps } from '../../../contextree/types';
import type { C11nEnv } from '../../../interpreter/c11n-env';

import { supportedGrammar, expressionHandlers } from './definitions';
import Parser from './parser';
import Lexer from './lexer';
import type { AST, GrammarList } from './types';
import { isOperationTypeSupported } from './utils';

/**
 * [Expression]
 * Description : Creates an instance for the flat expression string on which different actions
 * can be performed which are part of the evaluation process.
 * @class
 */
class Expression {
  public readonly _grammar: GrammarList;
  public readonly _expression: string;
  public _ast: AST | null;
  public _context: C11nEnv | undefined;
  public _contextTreeNodeContext?: NodeProps | null;
  public readonly _localDataObject: { [key: string]: string } | undefined;

  /**
   * Description : Expression instance of a flat expression string.
   * @class
   * @param expression Expression string.
   * @param localData data
   */
  constructor(expression: string, localData: object | undefined) {
    this._grammar = { ...supportedGrammar };
    this._expression = expression;
    this._ast = null;
    this._contextTreeNodeContext = null;
    this._localDataObject = localData as { [key: string]: string };
  }

  /**
   * [buildAST]
   * Description : Builds the Abstract Syntax Tree for an expression.
   * @returns Returns the updated instance with the AST.
   */
  buildAST() {
    const lexer = new Lexer(this._grammar);
    const tokens = lexer.tokenize(this._expression);
    const parser = new Parser(this._grammar);
    parser.addTokens(tokens);
    this._ast = parser.buildAst();
    return this;
  }

  /**
   * [evaluate]
   * Description : Evaluates the AST of the expression and returns the result.
   * @param context     c11n object
   * @returns Evaluation result
   */
  evaluate(context?: C11nEnv, contextTreeNodeProps?: NodeProps): boolean {
    this._context = context;
    this._contextTreeNodeContext = contextTreeNodeProps;
    return this.evaluateAST(this._ast as AST) as boolean;
  }

  /**
   * [evaluateAST]
   * Description : Evaluates the AST of the expression and returns the result.
   * @param ast Abstract syntax tree
   * @returns Evaluation result
   */
  evaluateAST(ast: AST, leftType?: string): boolean | string | number {
    return (
      isOperationTypeSupported(ast.operationType, expressionHandlers) &&
      expressionHandlers[ast.operationType].call(this, ast, leftType)
    );
  }
}

export default Expression;
